import React, { useEffect, useMemo } from "react";
import { brandPalette } from "../../../../constants/brandPalette";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { closeCreateLesson } from "../../../../modules/admin-modules/models/Actions";
import MainModel from "../../../../components/Models/main-model";
import {
  adminModalBodyProps,
  adminModalContentProps,
  adminModalHeaderProps,
  adminSelectStyles,
} from "../../../../components/Models/admin-modal-theme";
import { useForm } from "react-hook-form";
import MainInput from "../../../../components/Form/input";
import Select from "react-select";
import {
  createLessonRequest,
  editLessonRequest,
} from "../../../../modules/admin-modules/lessons/Actions";
import { useState } from "react";
import { toast } from "react-toastify";
import { getAllChaptersRequest } from "../../../../modules/admin-modules/chapters/Actions";
import { getAllGroupsRequest } from "../../../../modules/admin-modules/groups/Actions";

const getPdfFile = (value) => {
  if (value instanceof File) return value;
  if (value instanceof FileList && value.length > 0) return value[0];
  return null;
};

const appendGroups = (formData, groups) => {
  groups?.forEach((val, idx) => {
    const id = typeof val === "object" ? val?.id : val;
    if (id != null && id !== "") formData.append(`groups[${idx}]`, id);
  });
};

// Server post_max_size is 8M; leave room for other form fields in the request.
const MAX_PDF_SIZE_BYTES = 7 * 1024 * 1024;

const formatFileSize = (bytes) => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.ceil(bytes / 1024)} KB`;
};

const AddLesson = ({ isAdd, data, setSelectedData }) => {
  const [selected, setSelected] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const openLesson = useSelector((state) => state.model.isOpen3);
  const chapters = useSelector((state) => state.chapters.chapters);
  const groups = useSelector((state) => state.groups.groups);
  const isLoading = useSelector((state) => state.lessons.isLoading);

  const groupsOptions = groups?.map((data) => {
    return { value: data.id, label: data.name };
  });

  const chaptersOptions = chapters?.map((data) => {
    return { value: data.id, label: data.name };
  });
  const dispatch = useDispatch();
  useEffect(() => {
    if (openLesson) {
      dispatch(getAllChaptersRequest());
      dispatch(getAllGroupsRequest());
    }
  }, [openLesson]);

  const selectStyles = useMemo(() => adminSelectStyles({ menuZIndex: 50 }), []);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: data ? data : {},
    shouldUnregister: true,
  });

  const fileField = register("file");

  const header = (
    <Box>
      <Heading
        fontSize="32px"
        fontWeight="800"
        color="var(--text-primary)"
        fontFamily="'Amiri','Tajawal',sans-serif"
      >
        {isAdd ? "Add PDF Lesson" : "Edit PDF Lesson"}
      </Heading>
      <Text
        fontSize="xs"
        mt={1}
        color="var(--text-muted)"
        textTransform="uppercase"
        letterSpacing="0.14em"
        fontWeight="700"
      >
        Upload a file and assign chapter and groups
      </Text>
    </Box>
  );
  const onSubmit = async (values) => {
    const merged = { ...getValues(), ...values };
    const action = () => {
      dispatch(closeCreateLesson());
      setSelectedData({});
      reset();
      setSelected("");
      setSelectedFile(null);
    };

    if (isAdd) {
      const file = getPdfFile(selectedFile) ?? getPdfFile(merged.file);
      if (!file) {
        toast.error("Please select a PDF file.");
        return;
      }
      if (file.size > MAX_PDF_SIZE_BYTES) {
        toast.error(
          `PDF is too large (${formatFileSize(file.size)}). Maximum allowed size is ${formatFileSize(MAX_PDF_SIZE_BYTES)}.`
        );
        return;
      }
      if (!merged.chapter_id) {
        toast.error("Please select a chapter.");
        return;
      }
      if (!merged.groups?.length) {
        toast.error("Please select at least one group.");
        return;
      }

      const formData = new FormData();
      formData.append("title", merged.title ?? "");
      formData.append("over_view", merged.over_view ?? "");
      formData.append("chapter_id", merged.chapter_id);
      appendGroups(formData, merged.groups);
      formData.append("file", file);

      await dispatch(createLessonRequest(formData, action));
      return;
    }

    const formData = new FormData();
    formData.append("_method", "PUT");
    const skip = new Set([
      "id",
      "chapter",
      "groups",
      "created_at",
      "updated_at",
      "file",
    ]);
    const total = { ...data, ...merged };

    for (const key of Object.keys(total)) {
      if (skip.has(key)) continue;
      const v = total[key];
      if (v === undefined || v === null || v === "") continue;
      if (typeof v === "object" && !(v instanceof File) && !Array.isArray(v)) continue;
      formData.append(key, v);
    }

    const chapterId = merged.chapter_id ?? data?.chapter_id ?? data?.chapter?.id;
    if (chapterId != null) formData.set("chapter_id", chapterId);

    appendGroups(
      formData,
      merged.groups?.length ? merged.groups : data?.groups
    );

    const file = getPdfFile(selectedFile) ?? getPdfFile(merged.file);
    if (file) {
      if (file.size > MAX_PDF_SIZE_BYTES) {
        toast.error(
          `PDF is too large (${formatFileSize(file.size)}). Maximum allowed size is ${formatFileSize(MAX_PDF_SIZE_BYTES)}.`
        );
        return;
      }
      formData.set("file", file);
    }

    dispatch(
      editLessonRequest({
        id: data?.id,
        formData,
        action,
      })
    );
  };

  const closeFunc = () => {
    dispatch(closeCreateLesson());
    setSelectedData({});
    reset();
    setSelected("");
    setSelectedFile(null);
  };
  return (
    <MainModel
      isLoading={isLoading}
      header={header}
      closeFunc={closeFunc}
      openVar={openLesson}
      buttonTitle={null}
      buttonFunc={handleSubmit(onSubmit)}
      size="4xl"
      contentProps={adminModalContentProps}
      headerProps={adminModalHeaderProps}
      bodyProps={adminModalBodyProps}
      hideDefaultFooter
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={6} dir="ltr" w="100%">
          <SimpleGrid
            columns={{ base: 1, md: 2 }}
            spacingX={8}
            spacingY={6}
            alignItems="start"
          >
            <MainInput
              label="Lesson title"
              defaultValues={data?.title}
              error={errors?.title?.message}
              placeholder="Title shown in the library"
              register={register}
              name="title"
            />
            <Box gridColumn={{ base: "auto", md: "1 / -1" }}>
              <FormLabel
                m={0}
                fontSize="14px"
                fontWeight="700"
                color="var(--text-primary)"
                textAlign="left"
              >
                Overview
              </FormLabel>
              <Textarea
                mt={2}
                bg="var(--surface-muted)"
                color="var(--text-primary)"
                borderRadius="12px"
                border="1px solid"
                borderColor="var(--border-subtle)"
                defaultValue={data?.over_view}
                placeholder="Short description of the PDF"
                {...register("over_view")}
                name="over_view"
              />
            </Box>
            <Box gridColumn={{ base: "auto", md: "1 / -1" }}>
              <FormLabel
                m={0}
                mb={2}
                fontSize="14px"
                fontWeight="700"
                color="var(--text-primary)"
              >
                PDF file
              </FormLabel>
              <Button
                bg={brandPalette.primary}
                color="white"
                borderRadius="12px"
                textAlign="center"
                position="relative"
                w="100%"
                py={6}
                fontWeight="700"
                _hover={{ bg: brandPalette.accentDark }}
              >
                <Text>{selected ? selected : "Choose file to upload"}</Text>
                <Input
                  {...fileField}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > MAX_PDF_SIZE_BYTES) {
                      toast.error(
                        `PDF is too large (${formatFileSize(file.size)}). Maximum allowed size is ${formatFileSize(MAX_PDF_SIZE_BYTES)}.`
                      );
                      e.target.value = "";
                      setSelected("");
                      setSelectedFile(null);
                      return;
                    }
                    fileField.onChange(e);
                    setValue("file", file, { shouldDirty: true });
                    setSelected(file.name);
                    setSelectedFile(file);
                  }}
                  type="file"
                  accept="application/pdf,.pdf"
                  opacity="0"
                  position="absolute"
                  w="100%"
                  h="100%"
                  cursor="pointer"
                  top={0}
                  left={0}
                />
              </Button>
              <Text mt={2} fontSize="xs" color="var(--text-muted)">
                Maximum file size: {formatFileSize(MAX_PDF_SIZE_BYTES)} (PDF only)
              </Text>
            </Box>
            <FormControl>
              <VStack align="stretch" spacing={2}>
                <FormLabel
                  m={0}
                  fontSize="14px"
                  fontWeight="700"
                  color="var(--text-primary)"
                  textAlign="left"
                >
                  Chapter
                </FormLabel>
                <Select
                  options={chaptersOptions}
                  onChange={(d) => setValue("chapter_id", d.value)}
                  styles={selectStyles}
                  placeholder="Select chapter"
                  defaultValue={{
                    label: data?.chapter?.name,
                    value: data?.chapter?.id,
                  }}
                />
              </VStack>
            </FormControl>
            <FormControl gridColumn={{ base: "auto", md: "1 / -1" }}>
              <VStack align="stretch" spacing={2}>
                <FormLabel
                  m={0}
                  fontSize="14px"
                  fontWeight="700"
                  color="var(--text-primary)"
                  textAlign="left"
                >
                  Groups
                </FormLabel>
                <Select
                  options={groupsOptions}
                  isMulti
                  onChange={(d) => {
                    const selectedGroups = d.map((val) => val.value);
                    setValue("groups", selectedGroups);
                  }}
                  styles={selectStyles}
                  placeholder="Select groups"
                  defaultValue={data?.groups?.map((val) => ({
                    label: val?.name,
                    value: val?.id,
                  }))}
                />
              </VStack>
            </FormControl>
          </SimpleGrid>
          <Flex justify="flex-end" gap={6} pt={4}>
            <Button
              variant="ghost"
              color="var(--text-primary)"
              fontWeight="800"
              _hover={{ textDecoration: "underline", bg: "rgba(91, 13, 22, 0.08)" }}
              onClick={closeFunc}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              bg={brandPalette.primary}
              color="white"
              borderRadius="lg"
              px={8}
              py={6}
              fontWeight="800"
              isLoading={isLoading}
              _hover={{ bg: brandPalette.accentDark }}
              boxShadow="0 10px 24px rgba(91, 13, 22, 0.18)"
            >
              Save Changes
            </Button>
          </Flex>
        </Stack>
      </form>
    </MainModel>
  );
};

export default AddLesson;
